/**
 * Price service client for the app: every live price and chart candle comes
 * from prices.trigger.fun (pump-detector), never from exchanges directly.
 *
 *   getPriceStream()     shared WebSocket (live prices and candles)
 *   getPriceServiceRest() REST: markets, prices, candles
 *   getPriceMarkets()    venues listing a token, cached 30 min (60 s while
 *                        one of them has no price yet)
 *   peekPriceMarkets()   the cached venues only
 *   buildTradableTokens() every futures token, for the ticker picker
 */

import { TokenManager } from "@/utils/api-client-factory";
import { LocalStorageCacheStorage } from "@/lib/market-data/cache-storage";
import { ResultCache } from "@/lib/market-data/instrument-cache";
import { toServiceBase, type ServiceMarket } from "./exchanges";
import { PriceServiceRest } from "./price-service-rest";
import { PriceStream } from "./price-stream";
import { PriceTokenManager } from "./price-token";

const API_BASE_URL: string =
  (import.meta.env.VITE_API_URL as string | undefined) ||
  "https://main-api-production-1bb6.up.railway.app/api";

const MARKETS_TTL_MS = 30 * 60 * 1000;
/** A list with a venue the service does not price yet is asked about again after this long. */
const PARTLY_PRICED_MARKETS_TTL_MS = 60 * 1000;
/** A token nobody lists yet is asked about again after this long. */
const EMPTY_MARKETS_TTL_MS = 60 * 1000;

let tokens: PriceTokenManager | null = null;
let rest: PriceServiceRest | null = null;
let stream: PriceStream | null = null;
let marketsCache: ResultCache<ServiceMarket[]> | null = null;
let partlyPricedCache: ResultCache<ServiceMarket[]> | null = null;
const emptyMarkets = new Map<string, number>();
const inflightMarkets = new Map<string, Promise<ServiceMarket[]>>();

function getTokens(): PriceTokenManager {
  if (!tokens) {
    tokens = new PriceTokenManager({
      apiBaseUrl: API_BASE_URL,
      getJwt: () => TokenManager.getToken(),
    });
  }
  return tokens;
}

export function getPriceServiceRest(): PriceServiceRest {
  if (!rest) rest = new PriceServiceRest(getTokens());
  return rest;
}

export function getPriceStream(): PriceStream {
  if (!stream) {
    const created = new PriceStream({
      tokens: getTokens(),
      createSocket: (url) => new WebSocket(url),
      fetchPrices: (markets) => getPriceServiceRest().getPrices(markets),
    });
    if (typeof document !== "undefined") {
      document.addEventListener("visibilitychange", () => {
        if (document.visibilityState === "visible") created.reconnectNow();
      });
    }
    stream = created;
  }
  return stream;
}

function getMarketsCaches(): {
  full: ResultCache<ServiceMarket[]>;
  partial: ResultCache<ServiceMarket[]>;
} {
  if (!marketsCache || !partlyPricedCache) {
    const storage = new LocalStorageCacheStorage();
    storage.sweepExpired(MARKETS_TTL_MS, [
      "markets:",
      "markets-partial:",
      "avail:",
      "avail-all:",
    ]);
    marketsCache = new ResultCache<ServiceMarket[]>(MARKETS_TTL_MS, storage);
    partlyPricedCache = new ResultCache<ServiceMarket[]>(
      PARTLY_PRICED_MARKETS_TTL_MS,
      storage
    );
  }
  return { full: marketsCache, partial: partlyPricedCache };
}

/** Lists cached before `priced` was kept held priced venues only. */
function withPriced(markets: ServiceMarket[]): ServiceMarket[] {
  return markets.map((market) => ({
    ...market,
    priced: market.priced !== false,
  }));
}

function readCachedMarkets(base: string): ServiceMarket[] | null {
  const { full, partial } = getMarketsCaches();
  const cached =
    full.get(`markets:${base}`) ?? partial.get(`markets-partial:${base}`);
  return cached ? withPriced(cached) : null;
}

/** Cached venues for a token, without a request. */
export function peekPriceMarkets(symbol: string): ServiceMarket[] | null {
  return readCachedMarkets(toServiceBase(symbol));
}

/**
 * Venues (futures and spot) that list this token, each with whether the price
 * service prices it now. Cached 30 minutes when every venue is priced, else
 * 60 seconds so a venue that starts ticking shows up soon.
 */
export function getPriceMarkets(symbol: string): Promise<ServiceMarket[]> {
  const base = toServiceBase(symbol);
  const key = `markets:${base}`;

  const cached = readCachedMarkets(base);
  if (cached) return Promise.resolve(cached);

  const emptyAt = emptyMarkets.get(key);
  if (emptyAt && Date.now() - emptyAt < EMPTY_MARKETS_TTL_MS) {
    return Promise.resolve([]);
  }

  const inflight = inflightMarkets.get(key);
  if (inflight) return inflight;

  const request = getPriceServiceRest()
    .getMarkets(base)
    .then((markets) => {
      if (markets.length > 0) {
        const { full, partial } = getMarketsCaches();
        if (markets.every((market) => market.priced)) {
          full.set(key, markets);
        } else {
          partial.set(`markets-partial:${base}`, markets);
        }
        emptyMarkets.delete(key);
      } else {
        emptyMarkets.set(key, Date.now());
      }
      return markets;
    })
    .finally(() => {
      inflightMarkets.delete(key);
    });
  inflightMarkets.set(key, request);
  return request;
}

export * from "./exchanges";
export * from "./intervals";
export * from "./token-list";
export type { ServiceTokenList } from "./price-service-rest";
export type { FundingInfo, PriceUpdate, ServiceCandle } from "./price-service.types";
export { MAX_CANDLES_PER_REQUEST } from "./price-service-rest";
